import { Badge, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useDisclosure, useId } from '@mantine/hooks';
import { IconUsersGroup } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { getTravelRequestOverlapsOptions } from '../model/TravelRequestQuery';
import { TravelRequest } from '../model/TravelRequestTypes';
import { TravelOverlapDrawer, TravelOverlapErrorLine } from './TravelOverlapDrawer';

interface TravelOverlapStripProps {
  request: TravelRequest;
}

export function TravelOverlapStrip({ request }: TravelOverlapStripProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const [opened, { open, close }] = useDisclosure(false);
  const { data, error, isError, refetch } = useQuery(getTravelRequestOverlapsOptions(request.id));
  const errorLine = <TravelOverlapErrorLine onRetry={() => refetch()} />;
  // A refusal means this viewer may not read the matches, which a retry cannot change.
  const refused = isAxiosError(error) && error.response?.status === 403;

  if (data == null || data.length === 0) {
    return isError && !refused ? errorLine : null;
  }

  const exactCount = data.filter(overlap => overlap.exact).length;

  return (
    <Stack gap={4}>
      <Paper
        role="region"
        aria-labelledby={titleId}
        bg="yellow.0"
        withBorder
        radius="md"
        px="md"
        py="xs"
        style={{ borderColor: 'var(--mantine-color-yellow-3)' }}
      >
        <Group gap="sm">
          <IconUsersGroup
            size={20}
            color="var(--mantine-color-yellow-9)"
          />
          <Text
            id={titleId}
            size="sm"
            fw={600}
          >
            {t('travel.overlaps.stripTitle', { count: data.length })}
          </Text>
          <Group
            gap={6}
            flex={1}
          >
            {exactCount > 0 && (
              <Badge
                color="red"
                variant="light"
              >
                {t('travel.overlaps.exactCount', { count: exactCount })}
              </Badge>
            )}
          </Group>
          <Button
            size="xs"
            variant="default"
            onClick={open}
          >
            {t('travel.overlaps.compare')}
          </Button>
        </Group>
      </Paper>
      {isError && errorLine}
      <TravelOverlapDrawer
        requestId={request.id}
        opened={opened}
        onClose={close}
        source={request}
      />
    </Stack>
  );
}
